import { $getRoot, type LexicalEditor, type LexicalNode } from 'lexical';

import type { ParsedPause, ParsedReplacement } from './pause-tags';
import {
    findAllWordSequenceMatches,
    isSpacelessContent,
    matchInterleavedCharRuns,
    normalizeTranscriptToken,
    patternSegmentsFromPattern,
    type MatchableToken,
} from './transcript-word-match';

import {
    $createSilenceNode,
    $isSilenceNode,
    $isTranscriptWordNodeAndNotSilence,
    type SilenceNode,
} from 'js/src/data/transcript/nodes/SilenceNode';
import {
    $isTranscriptWordNode,
    type TranscriptWordNode,
} from 'js/src/data/transcript/nodes/TranscriptWordNode';
import {
    $getSilenceNodesAfter,
    $getSilenceNodesBefore,
    $isSpace,
    $isTrimmedOut,
    updateEditor,
} from 'js/src/data/transcript/transcript-utils';
import { TRANSCRIPT_CONFIG } from 'js/src/libs/consts';
import { lessThanWithinEpsilon } from 'js/src/luminary/comparison-utils';

export type ReconcileResult =
    | { userSelectedWordIds: string[]; changed: boolean }
    | { error: string };

/**
 * One entry in the transcript's token stream. A `<pause>` is just another token, like a word:
 * the read view the agent sees and the find_phrase/replacement it writes are both strings over
 * this same stream. A word token carries its node; a pause token carries the run of adjacent
 * SilenceNodes it coalesces (mirroring how getTranscriptPauses reads one pause per silence run).
 */
export type TranscriptToken =
    | { kind: 'word'; text: string; node: TranscriptWordNode }
    | { kind: 'pause'; nodes: SilenceNode[] };

/** A token in a find_phrase / replacement pattern. Pauses are presence-only (their N is ignored). */
export type PatternToken = { kind: 'word'; text: string } | { kind: 'pause' };

/** An inclusive slice of the token stream that a pattern matched. */
export type TokenSequenceMatch = { startIndex: number; endIndex: number };

/**
 * Whether a transcript node overlaps one of the group's Luminary cut regions (both in source time).
 *
 * Why read cut regions from Luminary rather than the editor's own TrimElementNodes: those trim nodes
 * are inserted by LuminarySyncPlugin on a React effect — i.e. asynchronously — so for the tick right
 * after a split/cut the editor still holds the full, untrimmed transcript. Luminary's cut regions
 * update synchronously, so reading them here keeps a just-split group reading as only its own span.
 */
function $isInCutRegion(node: LexicalNode, cutRegions: Array<[number, number]>): boolean {
    if (cutRegions.length === 0 || !$isTranscriptWordNode(node)) {
        return false;
    }
    const { startTime, endTime } = node;
    return cutRegions.some(
        ([cutStart, cutEnd]) =>
            lessThanWithinEpsilon(cutStart, endTime) && lessThanWithinEpsilon(startTime, cutEnd),
    );
}

/**
 * Build the interleaved word + pause token stream from the mounted editor, in the SAME order the
 * agent-facing transcript is rendered. Must run inside an editor read/update.
 *
 * Words are taken in editor node order; each run of adjacent SilenceNodes coalesces into ONE pause
 * token (matching getTranscriptPauses). Crucially, pauses are then interleaved among the words BY
 * TIME — a pause sits before the first word that starts after it — exactly as the read view does
 * (transcript-tokens.interleavePauses). A SilenceNode's node position and its timestamp can drift
 * apart after re-synthesis, so ordering by time (not raw node adjacency) is what keeps the stream
 * the agent matches against identical to the transcript it was shown.
 *
 * `cutRegions` excludes cut-away words up front (see {@link $isInCutRegion}); omit to use the editor's trim state.
 */
export function $buildTokenStream(cutRegions: Array<[number, number]> = []): TranscriptToken[] {
    const words: { startTime: number; text: string; node: TranscriptWordNode }[] = [];
    const pauseRuns: { startTime: number; nodes: SilenceNode[] }[] = [];
    let currentRun: { startTime: number; nodes: SilenceNode[] } | null = null;

    for (const node of $getRoot().getAllTextNodes()) {
        if ($isTrimmedOut(node) || $isSpace(node) || $isInCutRegion(node, cutRegions)) {
            continue;
        }
        if ($isSilenceNode(node)) {
            if (currentRun) {
                currentRun.nodes.push(node);
            } else {
                currentRun = { startTime: node.startTime, nodes: [node] };
                pauseRuns.push(currentRun);
            }
        } else if ($isTranscriptWordNodeAndNotSilence(node)) {
            currentRun = null;
            words.push({ startTime: node.startTime, text: node.getTextContent(), node });
        }
    }

    const tokens: TranscriptToken[] = [];
    let pauseIndex = 0;
    for (const word of words) {
        while (pauseIndex < pauseRuns.length && pauseRuns[pauseIndex].startTime < word.startTime) {
            tokens.push({ kind: 'pause', nodes: pauseRuns[pauseIndex].nodes });
            pauseIndex++;
        }
        tokens.push({ kind: 'word', text: word.text, node: word.node });
    }
    while (pauseIndex < pauseRuns.length) {
        tokens.push({ kind: 'pause', nodes: pauseRuns[pauseIndex].nodes });
        pauseIndex++;
    }
    return tokens;
}

/** Read the editor's token stream (wraps $buildTokenStream); pass `cutRegions` to exclude cut-away words. */
export function buildTokenStreamFromEditor(
    editor: LexicalEditor,
    cutRegions: Array<[number, number]> = [],
): TranscriptToken[] {
    let tokens: TranscriptToken[] = [];
    editor.getEditorState().read(() => {
        tokens = $buildTokenStream(cutRegions);
    });
    return tokens;
}

/**
 * Turn a parsed find_phrase / replacement into pattern tokens: words and pauses interleaved in
 * document order, with each pause placed after the word its afterWordIndex points at.
 */
export function buildPatternTokens({ words, pauses }: ParsedReplacement): PatternToken[] {
    const tokens: PatternToken[] = [];
    for (let i = 0; i <= words.length; i++) {
        for (const pause of pauses) {
            if (pause.afterWordIndex === i) {
                tokens.push({ kind: 'pause' });
            }
        }
        if (i < words.length) {
            tokens.push({ kind: 'word', text: words[i] });
        }
    }
    return tokens;
}

function tokensMatch(streamToken: TranscriptToken, patternToken: PatternToken): boolean {
    if (patternToken.kind === 'pause') {
        // A pause matches any pause, presence-only — the requested N is ignored on match.
        return streamToken.kind === 'pause';
    }
    if (streamToken.kind !== 'word') {
        return false;
    }
    return (
        normalizeTranscriptToken(streamToken.text) === normalizeTranscriptToken(patternToken.text)
    );
}

/**
 * Find every place a pattern occurs in the token stream as a strict, literal run: words match
 * words (with the usual case/punctuation tolerance), a pause token matches a present pause, and
 * any misalignment fails the run. Because the match is strict, a find_phrase that omits an
 * in-region pause simply does not match here (a loud failure) rather than silently dropping it.
 */
export function findTokenSequenceMatches(
    stream: TranscriptToken[],
    pattern: PatternToken[],
): TokenSequenceMatch[] {
    if (pattern.length === 0) {
        return [];
    }
    const matches: TokenSequenceMatch[] = [];
    for (let i = 0; i <= stream.length - pattern.length; i++) {
        let matched = true;
        for (let j = 0; j < pattern.length; j++) {
            if (!tokensMatch(stream[i + j], pattern[j])) {
                matched = false;
                break;
            }
        }
        if (matched) {
            matches.push({ startIndex: i, endIndex: i + pattern.length - 1 });
        }
    }
    if (matches.length > 0) {
        return matches;
    }

    // Fallback for spaceless scripts (CJK, Thai, …): match runs of word tokens by concatenated
    // characters so a request whose grouping differs from the ASR's token boundaries still lands.
    // Only runs after strict finds nothing and only on a spaceless stream, so Latin is a strict
    // no-op. Projecting the stream 1:1 keeps the returned indices valid `stream` indices for
    // matchRangeForTokens.
    const wordConcat = stream
        .filter((token) => token.kind === 'word')
        .map((token) => normalizeTranscriptToken(token.text))
        .join('');
    if (!isSpacelessContent(wordConcat)) {
        return matches;
    }
    const projected: MatchableToken[] = stream.map((token) =>
        token.kind === 'word' ? { word: token.text } : { word: '', isPause: true },
    );
    // A punctuation-only token normalizes to '' and is absorbed mid-run (correct), but must not
    // SEED a run — otherwise a leading '。' yields a duplicate start and a false ambiguity. The
    // same span is always also found starting on the following real word, so dropping these loses
    // nothing. (The other two fallbacks drop such tokens up front; this one keeps stream indices.)
    return matchInterleavedCharRuns(projected, patternSegmentsFromPattern(pattern)).filter(
        ({ startIndex }) => {
            const token = stream[startIndex];
            return token.kind === 'pause' || normalizeTranscriptToken(token.text) !== '';
        },
    );
}

/** Reported when the just-reworded span can no longer be found by its replacement words. */
export const SPAN_NOT_FOUND_ERROR = 'could not locate the reworded span in the transcript editor.';

export type MatchRange = {
    // How many word tokens precede the match's first word. Stage 1's reword only changes the
    // matched span, never the words before it, so this offset is stable across the reword and lets
    // stage 2 re-locate the (re-synthesized, freshly-id'd) span by POSITION — not by node ids,
    // which the correction flow regenerates.
    wordsBefore: number;
    // Whether the matched run itself began / ended with a pause token — i.e. whether find_phrase
    // explicitly claimed the pause at that outer edge. The gap just inside a range edge is only
    // reconciled when the match owned it (or when it is currently empty, so a brand-new pause can
    // still be added); an existing pause at an unclaimed edge is left exactly where it is. Internal
    // gaps are always reconciled — strict matching already forced find_phrase to include every
    // pause between the matched words.
    ownsLeadingGap: boolean;
    ownsTrailingGap: boolean;
};

/**
 * Resolve the word offset and edge ownership for a matched run. Any pause tokens strictly between
 * the matched words are internal to the range; a pause token at the very start/end of the match is
 * an owned edge pause.
 */
export function matchRangeForTokens(
    stream: TranscriptToken[],
    match: TokenSequenceMatch,
): MatchRange {
    let wordsBefore = 0;
    for (let i = 0; i < match.startIndex; i++) {
        if (stream[i].kind === 'word') {
            wordsBefore++;
        }
    }
    return {
        wordsBefore,
        ownsLeadingGap: stream[match.startIndex].kind === 'pause',
        ownsTrailingGap: stream[match.endIndex].kind === 'pause',
    };
}

/**
 * The number of whole-second SilenceNodes a desired pause maps to, matching the transcript UI's
 * Add-pause control (one node per second, minimum 1, capped at the UI's maximum). Fractional
 * requests are rounded to whole seconds because the UI/synthesis pipeline only authors whole
 * seconds — a 2.3s pause reads back as 2.3s only when it came from real recorded silence.
 */
function nodeCountForMs(ms: number): number {
    if (ms <= 0) {
        return 0;
    }
    const seconds = Math.round(ms / TRANSCRIPT_CONFIG.SILENCE_NODE_TARGET_DURATION_MILLIS);
    return Math.min(Math.max(1, seconds), TRANSCRIPT_CONFIG.MAX_SILENCE_NODES);
}

/**
 * Make the pauses inside a reworded span match `desiredPauses` (add / resize / remove), leaving the
 * span's words untouched. Positions are gaps in the span's word list: index 0 is before the first
 * word, index k is between words k-1 and k, index N is after the last word.
 *
 * The span is re-located in the (possibly re-synthesized) editor by matching `spanWords` — the
 * replacement's own words — and choosing the occurrence whose word offset is closest to
 * `wordOffsetHint` (the offset the match sat at before the reword). Matching by text rather than by
 * captured node ids is deliberate: the correction flow regenerates word-node ids, so ids captured
 * before the reword no longer exist afterwards. The offset hint disambiguates when the replacement
 * words also occur elsewhere, so a non-unique replacement is placed correctly instead of failing.
 *
 * Internal gaps are always reconciled — strict token matching already guaranteed find_phrase
 * referenced every pause between the matched words. A span EDGE gap (position 0 / position N) is
 * only reconciled when the match owned that edge (find_phrase began / ended with a pause) or the
 * edge is currently empty; an existing pause at an unclaimed edge is left untouched, so a reword
 * that names only an adjacent word never silently deletes the pause beside it.
 *
 * Silence is created exactly as the transcript UI's Add-pause control does: N zero-width
 * SilenceNodes (start === end) at the anchor, one per whole second, which the backend then
 * synthesizes into ~1s of silence each. Reconciliation compares by node count, so a request that
 * matches the current whole-second count is a no-op.
 *
 * Returns the ids of the span's words and the new silence nodes (to mark user-selected so the span
 * is re-synthesized), plus whether anything actually changed.
 */
export async function setPausesInRange(
    editor: LexicalEditor,
    {
        spanWords,
        wordOffsetHint,
        ownsLeadingGap = false,
        ownsTrailingGap = false,
        desiredPauses,
        onSilenceRemoved,
    }: {
        spanWords: string[];
        wordOffsetHint: number;
        ownsLeadingGap?: boolean;
        ownsTrailingGap?: boolean;
        desiredPauses: ParsedPause[];
        onSilenceRemoved?: () => void;
    },
): Promise<ReconcileResult> {
    let result: ReconcileResult = { userSelectedWordIds: [], changed: false };

    await updateEditor(editor, () => {
        const allWords = $getRoot()
            .getAllTextNodes()
            .filter(
                (node): node is TranscriptWordNode =>
                    $isTranscriptWordNodeAndNotSilence(node) && !$isSpace(node),
            );

        // Strict-only: the reconcile loop below maps desiredPauses' afterWordIndex (into the
        // replacement's word list) onto matched-node gaps, which only holds when the match's node
        // count equals the request's word count. The spaceless char-run fallback deliberately
        // regroups, breaking that alignment, so it must not run here — a miss stays a loud
        // SPAN_NOT_FOUND rather than silently misplacing a pause.
        const matches = findAllWordSequenceMatches(
            allWords.map((node) => ({ word: node.getTextContent() })),
            [spanWords.join(' ')],
            { allowSpacelessFallback: false },
        );
        if (matches.length === 0) {
            result = { error: SPAN_NOT_FOUND_ERROR };
            return;
        }
        // Pick the occurrence nearest the offset the span sat at before the reword, so a
        // replacement whose words also appear elsewhere still lands on the just-edited span.
        const match = matches.reduce((best, candidate) =>
            Math.abs(candidate.startIndex - wordOffsetHint) <
            Math.abs(best.startIndex - wordOffsetHint)
                ? candidate
                : best,
        );

        const nodes = allWords.slice(match.startIndex, match.endIndex + 1);
        if (nodes.length === 0) {
            // Nothing to hang pauses on (e.g. every word in the range was removed). Leave the
            // surrounding silences exactly as they are rather than guessing where they belong.
            result = { userSelectedWordIds: [], changed: false };
            return;
        }

        const desiredCountAt = (position: number): number =>
            nodeCountForMs(
                desiredPauses
                    .filter((pause) => pause.afterWordIndex === position)
                    .reduce((sum, pause) => sum + pause.ms, 0),
            );

        const $existingSilenceAt = (position: number) =>
            position === 0
                ? $getSilenceNodesBefore(nodes[0])
                : $getSilenceNodesAfter(nodes[position - 1]);

        let changed = false;
        let removedAny = false;
        const newSilenceIds: string[] = [];

        const $addSilenceNodes = (count: number, anchor: TranscriptWordNode, leading: boolean) => {
            const time = leading ? anchor.startTime : anchor.endTime;
            let ref: TranscriptWordNode = anchor;
            for (let i = 0; i < count; i++) {
                const silence = $createSilenceNode(time, time);
                if (leading) {
                    anchor.insertBefore(silence);
                } else {
                    ref.insertAfter(silence);
                    ref = silence;
                }
                silence.setActiveState(anchor.activeState);
                newSilenceIds.push(silence.id);
            }
        };

        for (let position = 0; position <= nodes.length; position++) {
            const existing = $existingSilenceAt(position);
            const desiredCount = desiredCountAt(position);

            const edgeOwned =
                position === 0
                    ? ownsLeadingGap
                    : position === nodes.length
                      ? ownsTrailingGap
                      : true; // internal gaps are always in scope
            // An existing pause at an unclaimed edge is left exactly as it is — the match never
            // referenced it, so it must not be removed or resized. (A brand-new pause can still be
            // added at an empty edge.)
            if (!edgeOwned && existing.length > 0) {
                continue;
            }

            if (existing.length === desiredCount) {
                continue;
            }
            changed = true;

            existing.forEach((node) => node.delete());
            removedAny = removedAny || existing.length > 0;

            if (desiredCount === 0) {
                continue;
            }
            $addSilenceNodes(
                desiredCount,
                position === 0 ? nodes[0] : nodes[position - 1],
                position === 0,
            );
        }

        if (!changed) {
            result = { userSelectedWordIds: [], changed: false };
            return;
        }
        if (removedAny) {
            onSilenceRemoved?.();
        }
        result = {
            userSelectedWordIds: [...nodes.map((node) => node.id), ...newSilenceIds],
            changed: true,
        };
    });

    return result;
}
